export interface Currency {
    code: string;
    name: string;
    symbol: string;
    flag: string;
}

export const CURRENCIES: Currency[] = [
    { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
    { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
    { code: 'TRY', name: 'Turkish Lira', symbol: '₺', flag: '🇹🇷' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', flag: '🇸🇦' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
    { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', flag: '🇰🇼' },
    { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع', flag: '🇴🇲' },
    { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', flag: '🇶🇦' },
];

export const DEFAULT_CURRENCY = 'USD';

// Exchange rates (in real app, fetch from API)
const EXCHANGE_RATES: Record<string, number> = {
    USD: 1,
    EUR: 0.85,
    TRY: 27.5,
    SAR: 3.75,
    AED: 3.67,
    KWD: 0.31,
    OMR: 0.38,
    QAR: 3.64,
};

export class CurrencyService {
    private static instance: CurrencyService;
    private currentCurrency: string = DEFAULT_CURRENCY;
    private listeners: Array<(currency: string) => void> = [];

    static getInstance(): CurrencyService {
        if (!CurrencyService.instance) {
            CurrencyService.instance = new CurrencyService();
        }
        return CurrencyService.instance;
    }

    constructor() {
        // Load from localStorage
        const saved = localStorage.getItem('selectedCurrency');
        if (saved && CURRENCIES.find(c => c.code === saved)) {
            this.currentCurrency = saved;
        }
    }

    getCurrentCurrency(): string {
        return this.currentCurrency;
    }

    setCurrency(code: string): void {
        if (CURRENCIES.find(c => c.code === code)) {
            this.currentCurrency = code;
            localStorage.setItem('selectedCurrency', code);
            this.notifyListeners();
        }
    }

    getCurrencyInfo(code?: string): Currency {
        const currencyCode = code || this.currentCurrency;
        return CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
    }

    convertPrice(amount: number, fromCurrency: string = 'USD', toCurrency?: string): number {
        const target = toCurrency || this.currentCurrency;
        
        if (fromCurrency === target) return amount;
        
        // Convert to USD first if needed
        const usdAmount = amount / EXCHANGE_RATES[fromCurrency];
        
        // Convert from USD to target
        return usdAmount * EXCHANGE_RATES[target];
    }

    formatPrice(amount: number, fromCurrency: string = 'USD'): string {
        const convertedAmount = this.convertPrice(amount, fromCurrency);
        const currency = this.getCurrencyInfo();
        
        return `${currency.symbol}${convertedAmount.toFixed(2)}`;
    }

    subscribe(listener: (currency: string) => void): () => void {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.currentCurrency));
    }
}

export const currencyService = CurrencyService.getInstance();

